import { errorMessageComponent } from "../../../shared/components/error/ErrorMessageComponent";
import { OrtomosaicoEstado } from "../../../shared/response/terceros/FincaResponse";
import { OrtomosaicoPendienteItem } from "../../../shared/response/terceros/OrtomosaicoPendienteItem";
import { FincasPlataformaService } from "../../../shared/generated/grpc/plataforma_terceros_fincas_pb";
import { grpcCall, grpcClient } from "../../../shared/services/grpc/grpcCall";

/** Se avisa con el estado final para que quien esté mostrando esa finca se repinte. */
export type OrtomosaicoListener = (fincaId: string, estado: OrtomosaicoEstado) => void;

/**
 * Vigila las publicaciones de ortomosaico en curso y avisa por toast cuando terminan.
 *
 * Consulta `GET /terceros/fincas/ortomosaicos-pendientes` (un request por ciclo, no uno
 * por finca) cada 45 s mientras haya algo pendiente, y se apaga cuando el listado vuelve
 * vacío. No tiene sentido ir más rápido: el worker cicla cada 5 min.
 *
 * CÓMO SE DECIDE QUÉ NOTIFICAR — son dos casos distintos y ninguno es "está en la lista":
 *
 *  - **Publicado**: el listado solo trae PENDIENTE y ERROR, así que una finca que se
 *    publica simplemente desaparece. Se detecta comparando con el ciclo anterior. Eso
 *    vive en memoria: una publicación que ocurre con la pestaña cerrada no se avisa.
 *  - **Falló**: una finca en ERROR **se queda en el listado** hasta que le vuelvan a
 *    subir el ZIP. Notificar por presencia re-avisaría los mismos errores en cada
 *    sesión, así que se usa el watermark: solo se avisa lo que trae un
 *    `ultimaActualizacion` mayor al último procesado, que se persiste en localStorage.
 */
export class OrtomosaicoWatchService {
  static readonly instance = new OrtomosaicoWatchService();
  private constructor() { }

  /** El worker cicla cada 5 min; consultar más seguido solo gasta requests. */
  private static readonly INTERVALO_MS = 45_000;
  /** Único dato no-sesión que la app persiste en el navegador (ver CLAUDE.md). */
  private static readonly WATERMARK_KEY = "sx.ortomosaico.watermark";

  private readonly _listeners = new Set<OrtomosaicoListener>();
  /** Snapshot del ciclo anterior: `fincaId → nombreFinca` de las que estaban pendientes. */
  private _pendientesPrevias = new Map<string, string>();
  private _timer: ReturnType<typeof setInterval> | null = null;
  private _revisando = false;

  /**
   * Arranca la vigilancia. Idempotente: llamarla de nuevo no duplica el ciclo, solo
   * fuerza una consulta inmediata. Se llama al iniciar sesión y al encolar un ZIP.
   */
  start(): void {
    void this._revisar();
    this._timer ??= setInterval(() => void this._revisar(), OrtomosaicoWatchService.INTERVALO_MS);
  }

  /** Devuelve la función para desuscribirse; llamarla en el `beforeDispose` de la página. */
  subscribe(listener: OrtomosaicoListener): () => void {
    this._listeners.add(listener);
    return () => this._listeners.delete(listener);
  }

  /**
   * Publicaciones de ortomosaico en curso (PENDIENTE) y fallidas (ERROR) de toda la organización
   * (`ListOrtomosaicosPendientes`), de la más reciente a la más antigua. La usan la vigilancia y el Dashboard.
   * Solo para quien ve Clientes: el Server rechaza la consulta a los demás roles.
   */
  async listarPendientes(): Promise<OrtomosaicoPendienteItem[]> {
    const response = await grpcCall(() =>
      grpcClient(FincasPlataformaService).listOrtomosaicosPendientes({}),
    );
    return response.items.map((i) => ({
      id: i.id,
      nombreFinca: i.nombreFinca,
      ortomosaicoEstado: i.ortomosaicoEstado as OrtomosaicoEstado,
      ortomosaicoError: i.ortomosaicoError ?? null,
      ultimaActualizacion: Number(i.ultimaActualizacion),
    }));
  }

  /** Un ciclo de la vigilancia: consulta las pendientes y avisa lo que terminó. Nunca se solapa con otro ciclo. */
  private async _revisar(): Promise<void> {
    if (this._revisando) return; // un ciclo lento no debe solaparse con el siguiente
    this._revisando = true;
    try {
      this._procesar(await this.listarPendientes());
    } catch (error) {
      // Un fallo de red no apaga la vigilancia: se reintenta en el ciclo siguiente.
      console.warn("[OrtomosaicoWatchService._revisar] no se pudieron consultar los ortomosaicos pendientes; se reintenta en el próximo ciclo", error);
    } finally {
      this._revisando = false;
    }
  }

  private _procesar(items: OrtomosaicoPendienteItem[]): void {
    const watermark = this._leerWatermark();
    const pendientes = new Map<string, string>();
    let mayor = watermark;

    for (const item of items) {
      if (item.ortomosaicoEstado === OrtomosaicoEstado.PENDIENTE) {
        pendientes.set(item.id, item.nombreFinca);
      } else if (item.ortomosaicoEstado === OrtomosaicoEstado.ERROR && item.ultimaActualizacion > watermark) {
        errorMessageComponent.addError(
          "Falló la publicación del ortomosaico",
          `${item.nombreFinca}: ${item.ortomosaicoError ?? "sin detalle."}`,
        );
        this._avisar(item.id, OrtomosaicoEstado.ERROR);
      }
      if (item.ultimaActualizacion > mayor) mayor = item.ultimaActualizacion;
    }

    // Lo que estaba pendiente y ya no figura se publicó: es el único rastro que queda.
    for (const [fincaId, nombreFinca] of this._pendientesPrevias) {
      if (pendientes.has(fincaId)) continue;
      if (items.some((item) => item.id === fincaId)) continue; // sigue ahí, pero en ERROR
      errorMessageComponent.addSuccess("Ortomosaico publicado", `${nombreFinca} ya tiene su capa disponible.`);
      this._avisar(fincaId, OrtomosaicoEstado.LISTO);
    }

    this._pendientesPrevias = pendientes;
    if (mayor > watermark) this._guardarWatermark(mayor);
    if (items.length === 0) this._detener();
  }

  private _avisar(fincaId: string, estado: OrtomosaicoEstado): void {
    this._listeners.forEach((listener) => listener(fincaId, estado));
  }

  // El acceso a localStorage va con guardas: en modo privado puede tirar excepción.
  private _leerWatermark(): number {
    try {
      return Number(localStorage.getItem(OrtomosaicoWatchService.WATERMARK_KEY)) || 0;
    } catch {
      return 0;
    }
  }

  private _guardarWatermark(valor: number): void {
    try {
      localStorage.setItem(OrtomosaicoWatchService.WATERMARK_KEY, String(valor));
    } catch {
      // Sin persistencia se re-notifican los errores viejos al recargar; no es fatal.
    }
  }

  private _detener(): void {
    if (this._timer === null) return;
    clearInterval(this._timer);
    this._timer = null;
  }
}
